import { inject, Injectable, signal,  computed } from '@angular/core';
import { FirestoreService } from './firestore.service';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { GiftPurchase, GiftPurchaseWithRegalo, Regalo, Solicitud } from '../models/regalo.interface';
import { orderBy, QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { AuthService } from './auth.service';
import { EstadisticasService } from './estadisticas.service';
import { collection, doc, writeBatch } from '@angular/fire/firestore';



@Injectable({ providedIn: 'root' })
export class RegaloService {
  private storage = inject(Storage);
  private firestoreService = inject(FirestoreService);
  private auth = inject(AuthService);
  private estadisticas = inject(EstadisticasService);

  public regalos = signal<Regalo[]>([]);
  public regalosRecibidos = signal<GiftPurchase[]>([]);
  public regalosRecibidosNoVistos = computed(() =>
    this.regalosRecibidos().filter(r => !r.visto)
  );

  public regalosWithDetails = computed<GiftPurchaseWithRegalo[]>(() =>
    this.regalosRecibidos().map(p => ({
      ...p,
      regalo: this.regalos().find(r => r.id === p.giftId)
    }))
  );

  private unsubRegalos!: () => void;
  private unsubCompras!: () => void;

  constructor() {
    // Catálogo de regalos
    this.unsubRegalos = this.firestoreService.listenCollectionQuery<Regalo>(
      'gifts',
      [orderBy('precio', 'asc')],
      docs => {
        const lista = docs.map(d => {
          const { docId, id, ...rest } = d;
          return { id: docId, ...rest };
        }) as Regalo[];
        this.regalos.set(lista);
      }
    );

    // Compras recibidas
    const uid = this.auth.uid;
    if (uid) {
      this.unsubCompras = this.firestoreService.listenCollectionQuery<GiftPurchase>(
        'giftPurchases',
        [where('toUserId', '==', uid), orderBy('fechaCompra', 'desc')],
        docs => {
          const lista = docs.map(d => ({ id: d.docId, ...d })) as GiftPurchase[];
          this.regalosRecibidos.set(lista);
        }
      );
    }
  }

  ngOnDestroy() {
    this.unsubRegalos?.();
    this.unsubCompras?.();
  }

  async subirImagen(file: File): Promise<string> {
    try {
      const path = `gifts/${Date.now()}_${file.name}`;
      const storageRef = ref(this.storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      return await getDownloadURL(storageRef);
    } catch (e) {
      console.error('[RegaloService] Error al subir imagen:', e);
      throw new Error('No se pudo subir la imagen');
    }
  }

  async crearRegalo(regalo: Regalo): Promise<void> {
    try {
      await this.firestoreService.addDoc<Regalo>('gifts', regalo);
    } catch (error) {
      console.error('[RegaloService] Error al crear regalo', error);
      throw error;
    }
  }

  async obtenerRegalos(): Promise<Regalo[]> {
    try {
      const constraints: QueryConstraint[] = [orderBy('precio', 'asc')];
      const docs = await this.firestoreService.getFilteredCollection<Regalo>('gifts', constraints);
      return docs.map(doc => {
        const { uid, id, ...rest } = doc;
        return { id: uid, ...rest };
      });
    } catch (error) {
      console.error('[RegaloService] Error al obtener regalos', error);
      throw error;
    }
  }

  async obtenerRegaloPorId(id: string): Promise<Regalo | null> {
    try {
      const data = await this.firestoreService.getDocById<Regalo>('gifts', id);
      return data ? { ...data, id } : null;
    } catch (error) {
      console.error(`[RegaloService] Error al obtener regalo ${id}`, error);
      throw error;
    }
  }

  async modificarRegalo(id: string, cambios: Partial<Regalo>): Promise<void> {
    try {
      await this.firestoreService.updateDoc<Regalo>('gifts', id, cambios);
    } catch (error) {
      console.error(`[RegaloService] Error al modificar regalo ${id}`, error);
      throw error;
    }
  }

  async eliminarRegalo(id: string): Promise<void> {
    try {
      await this.firestoreService.deleteDoc('gifts', id);
    } catch (error) {
      console.error(`[RegaloService] Error al eliminar regalo ${id}`, error);
      throw error;
    }
  }

  async comprarRegalo(regaloId: string, toUserId: string, fromUserName: string, toUserName: string): Promise<void> {
    const fromUserId = this.auth.uid;
    if (!fromUserId) throw new Error('Usuario no autenticado');
    if (fromUserId === toUserId) throw new Error('No puedes comprarte un regalo a ti mismo');

    try {
      const regalo = await this.obtenerRegaloPorId(regaloId);
      if (!regalo) throw new Error('Regalo no encontrado');

      const { precio, comisionPlataforma, imagenUrl, nombre } = regalo;
      const wallet = await this.firestoreService.getDocById<{ balance: number }>('userWallets', fromUserId);
      const balance = wallet?.balance ?? 0;
      if (balance < precio) throw new Error('Saldo insuficiente');

      await this.firestoreService.incrementField(`userWallets/${fromUserId}`, 'balance', -precio);

      const purchase: GiftPurchase = {
        giftId: regaloId,
        fromUserId,
        toUserId,
        precio,
        fromUserName,
        toUserName,
        comisionPlataforma,
        canjeado: false,
        nombreRegalo: nombre,
        visto: false,
        fotoRegaloUrl: imagenUrl,
        fechaCompra: Timestamp.now()
      };

      await this.firestoreService.addDoc<GiftPurchase>('giftPurchases', purchase);
      await this.estadisticas.incrementarCampo('totalRegalosComprados', 1);
    } catch (error) {
      console.error('[RegaloService] Error en compra de regalo', error);
      throw error;
    }
  }

  async marcarComoCanjeado(purchaseId: string): Promise<void> {
    try {
      await this.firestoreService.updateDoc<GiftPurchase>('giftPurchases', purchaseId, {
        canjeado: true,
        fechaCanje: Timestamp.now()
      });
    } catch (error) {
      console.error(`[RegaloService] Error al marcar como canjeado: ${purchaseId}`, error);
      throw error;
    }
  }

  async marcarComoVisto(purchaseId: string): Promise<void> {
    try {
      await this.firestoreService.updateDoc<GiftPurchase>('giftPurchases', purchaseId, {
        visto: true
      });
    } catch (error) {
      console.error(`[RegaloService] Error al marcar como visto: ${purchaseId}`, error);
      throw new Error('No se pudo marcar el regalo como visto');
    }
  }

  async obtenerRegalosEnviados(userId: string): Promise<GiftPurchase[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('fromUserId', '==', userId),
        orderBy('timestamp', 'desc')
      ];
      return await this.firestoreService.getFilteredCollection<GiftPurchase>('giftPurchases', constraints);
    } catch (error) {
      console.error(`[RegaloService] Error al obtener regalos enviados de ${userId}`, error);
      throw error;
    }
  }

  async obtenerRegalosRecibidos(userId: string): Promise<GiftPurchase[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('toUserId', '==', userId),
        orderBy('fechaCompra', 'desc')
      ];
      return await this.firestoreService.getFilteredCollection<GiftPurchase>('giftPurchases', constraints);
    } catch (error) {
      console.error(`[RegaloService] Error al obtener regalos recibidos de ${userId}`, error);
      throw error;
    }
  }

  async obtenerCompraPorId(purchaseId: string): Promise<GiftPurchase | null> {
    try {
      const data = await this.firestoreService.getDocById<GiftPurchase>('giftPurchases', purchaseId);
      return data ? { ...data, id: purchaseId } : null;
    } catch (error) {
      console.error(`[RegaloService] Error al obtener compra ${purchaseId}`, error);
      throw error;
    }
  }

  async obtenerRegalosRecibidosNoCanjeados(userId: string): Promise<GiftPurchase[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('toUserId', '==', userId),
        where('canjeado', '==', false),
        orderBy('fechaCompra', 'desc')
      ];
      return await this.firestoreService.getFilteredCollection<GiftPurchase>('giftPurchases', constraints);
    } catch (error) {
      console.error(`[RegaloService] Error al obtener regalos no canjeados de ${userId}`, error);
      throw error;
    }
  }

  async cobrarRegalos(userId: string): Promise<void> {
    try {
      // 1. Obtener regalos pendientes
      const regalos = await this.obtenerRegalosRecibidosNoCanjeados(userId);
      if (regalos.length === 0) {
        throw new Error('No hay regalos para cobrar');
      }
  
      // 2. Calcular total neto descontando comisión de plataforma
      const totalCobro = regalos.reduce((sum, r) => {
        const neto = r.precio - (r.precio * r.comisionPlataforma) / 100;
        return sum + neto;
      }, 0);
  
      const solicitud: Solicitud = {
        userId,
        totalCobro,
        fechaSolicitud: Timestamp.now(),
        estado: 'pendiente',
        regalos: regalos.map(r => r.uid!)
      };
  
      // 3. Crear batch
      const db = this.firestoreService.afs;
      const batch = writeBatch(db);
  
      // 4. Añadir la solicitud al batch
      const solRef = doc(collection(db, 'solicitudesCobro'));
      batch.set(solRef, solicitud);
  
      // 5. Incrementar estadística
      
      // 6. Marcar regalos como canjeados en el batch
      regalos.forEach(regalo => {
        if (regalo.uid) {
          const giftRef = doc(db, 'giftPurchases', regalo.uid);
          batch.update(giftRef, {
            canjeado: true,
            fechaCanje: Timestamp.now()
          });
        }
      });
      
      // 7. Commit único del batch
      await batch.commit();
      await this.estadisticas.incrementarCampo('totalSolicitudesCobro', 1);
      await this.estadisticas.incrementarCampo('totalRegalosCobrados', regalos.length);
  
    } catch (error) {
      console.error('[RegaloService] Error al cobrar regalos:', error);
      throw new Error('Error en el proceso de cobro de regalos');
    }
  }

  // Obtenemos Cobros Solicitados de todos los usuarios
  async obtenerCobrosSolicitados(): Promise<Solicitud[]> {
    try {
      const constraints: QueryConstraint[] = [
        orderBy('fechaSolicitud', 'desc'),
        where('estado', '==', 'pendiente')
      ];
      const docs = await this.firestoreService.getFilteredCollection<Solicitud>('solicitudesCobro', constraints);
      return docs.map(d => ({ id: d.uid, ...d }));
    } catch (error) {
      console.error('[RegaloService] Error al obtener cobros solicitados:', error);
      throw new Error('No se pudieron cargar las solicitudes de cobro.');
    }
  }
  
  /** Obtiene una solicitud por su ID */
  async obtenerSolicitudPorId(id: string): Promise<Solicitud | null> {
    try {
      const data = await this.firestoreService.getDocById<Solicitud>('solicitudesCobro', id);
      return data ? ({ id, ...data } as Solicitud) : null;
    } catch (error) {
      console.error(`[RegaloService] Error al obtener solicitud ${id}:`, error);
      throw new Error('No se pudo obtener la solicitud.');
    }
  }

  /** Procesa una solicitud: aprueba y marca regalos como procesados con fecha de pago */
  async procesarSolicitud(solicitudId: string): Promise<void> {
    try {
      const db = this.firestoreService.afs;
      const batch = writeBatch(db);
      const solRef = doc(db, 'solicitudesCobro', solicitudId);
      batch.update(solRef, { estado: 'aprobada' });

      // Carga la solicitud para obtener IDs de giftPurchases
      const solicitud = await this.obtenerSolicitudPorId(solicitudId);
      if (!solicitud) throw new Error('Solicitud no encontrada');

      solicitud.regalos.forEach(purchaseId => {
        const purchaseRef = doc(db, 'giftPurchases', purchaseId);
        batch.update(purchaseRef, {
          procesado: true,
          FechaPago: Timestamp.now()
        });
      });

      await batch.commit();
      await this.estadisticas.incrementarCampo('totalPagosEnviados', 1);
    } catch (error) {
      console.error(`[RegaloService] Error al procesar solicitud ${solicitudId}:`, error);
      throw new Error('No se pudo procesar la solicitud.');
    }
  }

  /** Rechaza una solicitud sin procesar regalos */
  async rechazarSolicitud(solicitudId: string): Promise<void> {
    try {
      await this.firestoreService.updateDoc<Solicitud>('solicitudesCobro', solicitudId, {
        estado: 'rechazada'
      });
    } catch (error) {
      console.error(`[RegaloService] Error al rechazar solicitud ${solicitudId}:`, error);
      throw new Error('No se pudo rechazar la solicitud.');
    }
  }
  
  
}
